import {
  Form,
  LargeInput,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"
import type {
  ChangeEventHandler,
  FormEventHandler,
  ReactNode,
} from "react"

import styles from "../AccountForms.module.scss"
import { RequirementLabel } from "../RequirementLabel"
import { DateOfBirthInput } from "./DateOfBirthInput"

import { usePolyglot } from "src/contexts"

const polyglotPrefix = "components.account_forms.personal_information."
const firstNameId = "first-name-id"
const lastNameId = "last-name-id"
const displayNameId = "display-name-id"

interface PersonalInformation {
  firstname: string;
  lastname: string;
  dateOfBirth: string;
  nickname: string;
}

interface Props {
  form: PersonalInformation;
  updateForm: (form: Partial<PersonalInformation>) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  children?: ReactNode;
}

export function PersonalInformationForm(props: Props) {
  const {
    children,
    onSubmit,
    form,
    updateForm,
  } = props

  const polyglot = usePolyglot()

  const onFirstNameChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        firstname: e.target.value,
      })
    },
    [updateForm],
  )

  const onLastNameChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        lastname: e.target.value,
      })
    },
    [updateForm],
  )

  const onDateOfBirthChange = useCallback(
    (dateOfBirth: string) => {
      updateForm({
        dateOfBirth,
      })
    },
    [updateForm],
  )

  const onDisplayNameChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        nickname: e.target.value,
      })
    },
    [updateForm],
  )

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <div className={styles.inputs}>
        <RequirementLabel variant="required" />
        <LargeInput
          id={firstNameId}
          placeholder={polyglot.t(`${polyglotPrefix}first_name.placeholder`)}
          className={styles.input}
          type="text"
          value={form.firstname}
          onChange={onFirstNameChange}
          autoComplete="given-name"
          required
        />
        <LargeInput
          id={lastNameId}
          placeholder={polyglot.t(`${polyglotPrefix}last_name.placeholder`)}
          className={styles.input}
          type="text"
          value={form.lastname}
          onChange={onLastNameChange}
          autoComplete="family-name"
          required
        />
        <DateOfBirthInput
          value={form.dateOfBirth}
          onChange={onDateOfBirthChange}
        />
        <RequirementLabel variant="optional" />
        <LargeInput
          id={displayNameId}
          placeholder={polyglot.t(`${polyglotPrefix}display_name.placeholder`)}
          className={styles.input}
          type="text"
          value={form.nickname}
          onChange={onDisplayNameChange}
          autoComplete="nickname"
          maxLength={60}
        />
      </div>
      {children}
    </Form>
  )
}
